import React from 'react'
import axios from 'axios'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Typography,
  Grid,
  CircularProgress
} from '@material-ui/core'
import { Close as CloseIcon } from '@material-ui/icons'

const PROJECT_TYPES = [
  { value: 'new_construction', label: 'New Construction' },
  { value: 'renovation', label: 'Renovation' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'cleanup', label: 'Cleanup' }
]

const NewProjectModal = ({ open, onClose, onSave, url }) => {
  const [name, setName] = React.useState('')
  const [client, setClient] = React.useState('')
  const [projectType, setProjectType] = React.useState('')
  const [buildings, setBuildings] = React.useState([])
  const [buildingInput, setBuildingInput] = React.useState('')
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState('')

  const resetForm = () => {
    setName('')
    setClient('')
    setProjectType('')
    setBuildings([])
    setBuildingInput('')
    setError('')
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const handleAddBuilding = () => {
    const trimmed = buildingInput.trim()
    if (trimmed) {
      setBuildings([...buildings, trimmed])
      setBuildingInput('')
    }
  }

  const handleRemoveBuilding = (index) => {
    setBuildings(buildings.filter((_, i) => i !== index))
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddBuilding()
    }
  }

  const handleSave = () => {
    if (!name.trim()) {
      setError('Project name is required')
      return
    }

    setSaving(true)
    setError('')

    axios.post(`${url}/api/bid-estimation/projects`, {
      name: name.trim(),
      client: client.trim(),
      projectType,
      buildings: buildings.map(b => ({ name: b }))
    }).then(() => {
      setSaving(false)
      resetForm()
      onSave()
    }).catch(err => {
      console.error(err)
      setSaving(false)
      setError(err.response?.data?.message || 'Failed to create project')
    })
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Typography variant="h6">New Project</Typography>
          </Grid>
          <Grid item>
            <IconButton size="small" onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent>
        <TextField
          label="Project Name"
          value={name}
          onChange={e => setName(e.target.value)}
          fullWidth
          required
          margin="normal"
          variant="outlined"
          error={!!error && !name.trim()}
          helperText={!name.trim() && error ? error : ''}
        />

        <TextField
          label="Client"
          value={client}
          onChange={e => setClient(e.target.value)}
          fullWidth
          margin="normal"
          variant="outlined"
        />

        <FormControl fullWidth margin="normal" variant="outlined">
          <InputLabel>Project Type</InputLabel>
          <Select
            value={projectType}
            onChange={e => setProjectType(e.target.value)}
            label="Project Type"
          >
            {PROJECT_TYPES.map(type => (
              <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography variant="subtitle2" style={{ marginTop: 16, marginBottom: 8 }}>
          Buildings
        </Typography>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs>
            <TextField
              label="Building Name"
              value={buildingInput}
              onChange={e => setBuildingInput(e.target.value)}
              onKeyPress={handleKeyPress}
              fullWidth
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleAddBuilding}
              disabled={!buildingInput.trim()}
            >
              Add
            </Button>
          </Grid>
        </Grid>

        {buildings.length > 0 && (
          <div style={{ marginTop: 8 }}>
            {buildings.map((building, index) => (
              <Grid
                container
                key={index}
                alignItems="center"
                spacing={1}
                style={{ marginTop: 4 }}
              >
                <Grid item xs>
                  <Typography variant="body2">{building}</Typography>
                </Grid>
                <Grid item>
                  <IconButton size="small" onClick={() => handleRemoveBuilding(index)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
          </div>
        )}

        {error && name.trim() && (
          <Typography color="error" variant="body2" style={{ marginTop: 12 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={saving}
        >
          {saving ? <CircularProgress size={24} /> : 'Create Project'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default NewProjectModal
